import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useApi, useCaller } from '@/hooks/api.hook';
import {
  markAllNotificationsReadAction,
  markNotificationsReadAction,
  setNotificationStatisticAction,
  setNotificationsAction,
  setNotificationsErrorAction,
} from '@/redux/slicers/notification.slice';
import {
  markNotificationDropdownAllReadAction,
  setNotificationDropdownAction,
  setNotificationDropdownErrorAction,
} from '@/redux/slicers/notification-dropdown.slice';
import type {
  Notification,
  NotificationApiResponse,
  NotificationContent,
  NotificationListContent,
  NotificationListRequest,
  NotificationPagination,
} from '@/types/notification';
import { normalizeNotification, toNotificationApiResponse } from '@/utils/notification';
import { generateUrlParams } from '@/utils/common';

const emptyPagination = (pageNumber: number, pageSize: number): NotificationPagination => ({
  pageNumber,
  pageSize,
  totalElements: 0,
  totalPages: 0,
  numberOfElements: 0,
  first: true,
  last: true,
});

const emptyNotificationContent = (pageNumber = 1, pageSize = 10): NotificationContent => ({
  items: [],
  unreadCount: 0,
  pagination: emptyPagination(pageNumber, pageSize),
});

const normalizeNotificationsPayload = (
  response: any,
  fallback: Required<Pick<NotificationListRequest, 'pageNumber' | 'pageSize'>>,
): NotificationContent => {
  const content = response?.content;
  const rawItems = Array.isArray(content)
    ? content
    : content?.items || content?.notifications || [];
  const items = (Array.isArray(rawItems) ? rawItems : []).map((item) => {
    // Some admin payloads may use `message` instead of `body`; keep it backward-compatible here.
    const patched =
      item && typeof item === 'object' ? { ...item, body: item.body ?? item.message } : item;
    return normalizeNotification(patched as Notification);
  });

  const pageNumber =
    response?.page ??
    (typeof response?.number === 'number' ? response.number + 1 : undefined) ??
    (Array.isArray(content) ? undefined : content?.pagination?.pageNumber) ??
    fallback.pageNumber;
  const pageSize =
    response?.size ??
    (Array.isArray(content) ? undefined : content?.pagination?.pageSize) ??
    fallback.pageSize;
  const totalElements =
    response?.totalElements ??
    (Array.isArray(content) ? undefined : content?.pagination?.totalElements) ??
    items.length;
  const totalPages =
    response?.totalPages ??
    (Array.isArray(content) ? undefined : content?.pagination?.totalPages) ??
    Math.ceil(totalElements / Math.max(1, pageSize));

  const unreadCount =
    response?.unreadCount ??
    (Array.isArray(content) ? undefined : content?.unreadCount) ??
    items.filter((notification) => !notification.isRead).length;

  const numberOfElements =
    response?.numberOfElements ??
    (Array.isArray(content) ? undefined : content?.pagination?.numberOfElements) ??
    items.length;

  const first =
    response?.first ??
    (Array.isArray(content) ? undefined : content?.pagination?.first) ??
    pageNumber <= 1;

  const last =
    response?.last ??
    (Array.isArray(content) ? undefined : content?.pagination?.last) ??
    pageNumber >= totalPages;

  return {
    items,
    unreadCount,
    pagination: {
      pageNumber,
      pageSize,
      totalElements,
      totalPages,
      numberOfElements,
      first,
      last,
    },
  };
};

const toListContent = (content: NotificationContent): NotificationListContent => ({
  notifications: content.items,
  unreadCount: content.unreadCount,
  totalCount: content.pagination.totalElements ?? content.items.length,
  hasMore: !content.pagination.last,
});

const getErrorMessage = (response: any, fallback: string) => {
  if (Array.isArray(response?.messages) && response.messages.length > 0) {
    return response.messages.join(', ');
  }

  return fallback;
};

// Admin-facing notification hook (used by header dropdown and /admin/notifications)
export const useNotificationService = () => {
  const api = useApi('');
  const caller = useCaller();
  const dispatch = useDispatch();

  const getNotifications = useCallback(
    async ({
      status,
      readStatus,
      pageNumber = 1,
      pageSize = 10,
      sort,
      search,
    }: NotificationListRequest = {}) => {
      const urlParams = generateUrlParams({
        Status: status ?? readStatus,
        PageNumber: pageNumber,
        PageSize: pageSize,
        Sort: sort,
        Search: search,
      });

      const response: any = await caller(
        () => api.get(`/api/v1/admin/notifications?${urlParams}`),
        {
          loadingKey: 'admin-notifications',
        },
      );

      if (response?.succeeded) {
        const normalized = normalizeNotificationsPayload(response, { pageNumber, pageSize });
        dispatch(
          setNotificationsAction({
            notifications: normalized.items,
            unreadCount: normalized.unreadCount,
            pagination: normalized.pagination,
          }),
        );
        return response as NotificationApiResponse<unknown>;
      }

      dispatch(
        setNotificationsAction({
          notifications: [],
          unreadCount: 0,
          pagination: emptyPagination(pageNumber, pageSize),
        }),
      );
      dispatch(setNotificationsErrorAction(getErrorMessage(response, 'Không thể tải thông báo.')));
      return response as NotificationApiResponse<unknown>;
    },
    [api, caller, dispatch],
  );

  const getNotificationDropdown = useCallback(
    async ({
      status,
      readStatus,
      pageNumber = 1,
      pageSize = 3,
      sort,
      search,
    }: NotificationListRequest = {}) => {
      const urlParams = generateUrlParams({
        Status: status ?? readStatus,
        PageNumber: pageNumber,
        PageSize: pageSize,
        Sort: sort,
        Search: search,
      });

      const response: any = await caller(
        () => api.get(`/api/v1/admin/notifications?${urlParams}`),
        {
          loadingKey: 'admin-notifications-dropdown',
        },
      );

      if (response?.succeeded) {
        dispatch(
          setNotificationDropdownAction(
            normalizeNotificationsPayload(response, { pageNumber, pageSize }),
          ),
        );
        return response as NotificationApiResponse<unknown>;
      }

      dispatch(setNotificationDropdownAction(emptyNotificationContent(pageNumber, pageSize)));
      dispatch(
        setNotificationDropdownErrorAction(getErrorMessage(response, 'Không thể tải thông báo.')),
      );
      return response as NotificationApiResponse<unknown>;
    },
    [api, caller, dispatch],
  );

  const getNotificationStatistics = useCallback(async () => {
    const response: any = await caller(() => api.get('/api/v1/admin/notifications/statistics'), {
      loadingKey: 'admin-notification-statistics',
    });

    if (response?.succeeded) {
      dispatch(setNotificationStatisticAction(response.content));
      return response as NotificationApiResponse<unknown>;
    }

    dispatch(setNotificationStatisticAction({ unreadCount: 0 }));
    dispatch(
      setNotificationsErrorAction(getErrorMessage(response, 'Không thể tải thống kê thông báo.')),
    );
    return response as NotificationApiResponse<unknown>;
  }, [api, caller, dispatch]);

  const markAsRead = useCallback(
    async (notificationIds: string[]) => {
      const ids = Array.from(new Set(notificationIds.filter(Boolean)));
      if (ids.length === 0) {
        return null;
      }

      const response: any = await caller(
        () => api.put('/api/v1/admin/notifications/read', { notificationIds: ids }),
        {
          loadingKey: 'admin-notifications-read',
        },
      );

      if (response?.succeeded) {
        dispatch(markNotificationsReadAction(ids));
        return response as NotificationApiResponse<unknown>;
      }

      dispatch(
        setNotificationsErrorAction(
          getErrorMessage(response, 'Không thể đánh dấu thông báo đã đọc.'),
        ),
      );
      return response as NotificationApiResponse<unknown>;
    },
    [api, caller, dispatch],
  );

  const markAllAsRead = useCallback(async () => {
    const response: any = await caller(() => api.put('/api/v1/admin/notifications/read-all'), {
      loadingKey: 'admin-notifications-read-all',
    });

    if (response?.succeeded) {
      dispatch(markAllNotificationsReadAction());
      dispatch(markNotificationDropdownAllReadAction());
      return response as NotificationApiResponse<unknown>;
    }

    dispatch(
      setNotificationsErrorAction(
        getErrorMessage(response, 'Không thể đánh dấu tất cả thông báo đã đọc.'),
      ),
    );
    return response as NotificationApiResponse<unknown>;
  }, [api, caller, dispatch]);

  // Kept for compatibility with older code paths that expect an API-like response shape.
  const getNotificationsApi = useCallback(
    async (request: NotificationListRequest = {}) => {
      const response = await caller(() => api.get('/api/v1/admin/notifications', request), {
        loadingKey: 'admin-notifications-api',
      });
      const content = normalizeNotificationsPayload(response, {
        pageNumber: request.pageNumber ?? 1,
        pageSize: request.pageSize ?? 10,
      });
      return toNotificationApiResponse(response, toListContent(content));
    },
    [api, caller],
  );

  return {
    getNotificationDropdown,
    getNotifications,
    getNotificationStatistics,
    markAsRead,
    markAllAsRead,
    // Optional helper; not used by current UI, but safe and can help transitional code.
    getNotificationsApi,
  };
};
